<!-- components/LazyImage.vue -->
<template>
  <img
    :src="imageSrc"
    :alt="alt"
    v-bind="$attrs"
    ref="imgRef"
    @error="onError"
    @load="onLoad"
    class="lazy-load-image"
    :class="props.class"
  />
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from "vue"

import defaultCover from "/assets/default-cover-3x.png"

const props = defineProps({
  src: {
    type: String,
    required: true,
  },
  alt: {
    type: String,
    default: "",
  },
  loading: {
    type: String,
    default: null,
  },
  error: {
    type: String,
    default: null,
  },
  class: {
    type: String,
    default: "",
  },
})

const imageSrc = ref(props.loading ?? defaultCover)
const imgRef = ref(null)
let observer = null

const loadImage = () => {
  imageSrc.value = props.src ?? defaultCover
}

const onError = () => {
  imageSrc.value = props.error ?? defaultCover
}

const onLoad = () => {
  // Optional: emit loaded event or apply CSS
}

onMounted(() => {
  if ("IntersectionObserver" in window) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadImage()
            observer.unobserve(entry.target)
          }
        })
      },
      {
        rootMargin: "100px",
      }
    )
    if (imgRef.value) {
      observer.observe(imgRef.value)
    }
  } else {
    // Fallback if IntersectionObserver not supported
    loadImage()
  }
})

onBeforeUnmount(() => {
  if (observer && imgRef.value) {
    observer.unobserve(imgRef.value)
  }
})

watch(
  () => props.src,
  (newSrc) => {
    if (newSrc) {
      imageSrc.value = props.loading ?? defaultCover
      if (observer && imgRef.value) {
        observer.observe(imgRef.value)
      }
    }
  },
)
</script>

<style scoped>
img.lazy-load-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
